// Which lines of the edit pane's live preview each field drives. Found by trying a change to
// the field and seeing which lines move, so no field has to be told what it feeds.
import type { BuildingNote } from "../../api/hooks/useAmazonBuildingNotes";
import type { LineMark } from "./CellCard";
import { type Kind, type Overrides, STATE_OPTIONS } from "./overrides";
import { CARD_LINES } from "./shownPart";

/** The preview's lines in the order they are lettered: the hover card's, then the Step 2
 * cell and the Step 3 card around it. */
const LINE_ORDER: readonly string[] = [...CARD_LINES, "cell", "card"];

export const LINE_NAMES: Record<string, string> = {
  status: "the status line",
  note: "the qualifier",
  lines: "the detail lines",
  more: "the details",
  proof: "the send line",
  offer: "the capacity line",
  items: "the Needed from Amazon list",
  line: "the extra line",
  cell: "the Step 2 cell",
  card: "the Step 3 card",
};

/** ``o`` with ``key`` written as ``text``, or cleared for null, as a save would leave it. */
export const withValue = (
  o: Overrides,
  projectId: string,
  key: string,
  text: string | null,
): Overrides => {
  const next = new Map<string, BuildingNote>(o);
  if (text === null) next.delete(key);
  else {
    next.set(key, {
      project_id: projectId,
      step_id: key,
      text,
      platform: null,
      version: 0,
      updated_at: "",
      updated_by: null,
    });
  }
  return next;
};

/** A value of ``kind`` other than ``v``: what a field is tried at to see what it moves. */
export const otherValue = (kind: Kind, v: string): string => {
  switch (kind) {
    case "state":
      return STATE_OPTIONS.find(([s]) => s !== v)?.[0] ?? "done";
    case "date":
      return v === "2000-01-02" ? "2000-01-03" : "2000-01-02";
    case "number":
      return String((Number(v) || 0) + 1);
    case "flag":
      return v === "true" ? "false" : "true";
    default:
      return `${v} (changed)`;
  }
};

export interface Links {
  mark: (key: string) => LineMark | undefined;
  /** The lines a field moves, in lettered order. */
  drives: (fieldKey: string) => string[];
  /** Whether a field moves anything the preview compares, lettered or not: a line, or the
   * Step 3 programme (``PLAN``), which the preview does not draw. */
  moves: (fieldKey: string) => boolean;
}

/** The preview's key for Step 3's programme dates: compared, never drawn or lettered. */
export const PLAN = "plan";

/** Letters the preview's lines and names the lines each field moves. ``now`` is the preview
 * with the form's values, ``saved`` with what is saved, ``tried`` each field's preview with
 * that field changed. ``letters`` carries the letters already given, so a line keeps its
 * letter while the form is open and a line that appears takes the next one. */
export const linkLines = (
  now: Map<string, string>,
  saved: Map<string, string>,
  tried: Map<string, Map<string, string>>,
  letters: Map<string, string>,
): Links => {
  const moved = (a: Map<string, string>): string[] =>
    LINE_ORDER.filter((k) => a.get(k) !== now.get(k));
  const drives = new Map([...tried].map(([f, t]) => [f, moved(t)]));
  const shown = new Set([...now.keys(), ...[...drives.values()].flat()]);
  for (const k of LINE_ORDER) {
    if (shown.has(k) && !letters.has(k)) letters.set(k, String.fromCharCode(65 + letters.size));
  }
  return {
    mark: (k) => {
      const letter = letters.get(k);
      return letter ? { letter, changed: now.get(k) !== saved.get(k) } : undefined;
    },
    drives: (f) => drives.get(f) ?? [],
    moves: (f) => {
      const t = tried.get(f);
      return Boolean(t) && [...new Set([...now.keys(), ...(t?.keys() ?? [])])].some(
        (k) => t?.get(k) !== now.get(k),
      );
    },
  };
};
